import React, { useState } from "react";
import moment from "moment";
import { Link } from "react-router-dom";
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table";
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css";
import { toast } from "react-toastify";
import Avatar from "../inputs/Avatar";
import Switch from "../inputs/Switch";
import {
  useInviteUserMutation,
  useUpdateUserMutation,
} from "../../services/organization/adminUsers";
import { getAccountStatus, secondsToHours } from "../../utils/helpers";
import EmptyState from "../EmptyState";
import Icon from "../icons/Icon";
import { UsersStrokeIcon } from "../icons/Icons";
import { useTranslation } from "react-i18next";

const EmptyEmployees = ({ t }) => (
  <EmptyState
    iconComponent={
      <Icon
        icon={<UsersStrokeIcon />}
        iconName="users"
        iconSize={38}
        viewBox="0 0 25 25"
        iconColor="#bbbabf"
        hasStroke
      />
    }
    title={t("emptyStates.employees")}
    emptyStateText={t("employees:addEmployeeToAppear")}
  />
);

const EmployeeActionsMenu = ({
  element,
  accountStatus,
  updateUser,
  t,
  menuId,
}) => (
  <div className="dropdown tools">
    <button
      className="dropdown-toggle"
      type="button"
      id={menuId}
      data-bs-toggle="dropdown"
      aria-expanded="false"
      aria-label={t("actions")}
    >
      <span className="fas fa-ellipsis-h fa-fw font_14 font-color-grey"></span>
    </button>
    <ul className="dropdown-menu dropdown-menu-end" aria-labelledby={menuId}>
      <li>
        <div>
          <p className="bold mb-0 font_12 font-extrabold">{element?.name}</p>
          <p className="mb-0 font_12 font-color-grey">{element?.email}</p>
        </div>
      </li>
      <li>
        <hr className="dropdown-divider" />
      </li>
      <li>
        <Link
          className="dropdown-item font_14 font-bold"
          to={`/admin/employee/${element?.id}`}
        >
          <span className="far fa-eye fa-fw font-color-grey me-2"></span>
          <span>{t("seeAccount")}</span>
        </Link>
      </li>
      <li>
        <Link className="dropdown-item font_14 font-bold" to="#">
          <span className="far fa-comment fa-fw font-color-grey me-2"></span>
          <span>{t("sendMessage")}</span>
        </Link>
      </li>
      <li>
        <hr className="dropdown-divider" />
      </li>
      <li>
        <Switch
          label={t("accountStatus")}
          disabled={accountStatus.type == "notResigtered"}
          checked={accountStatus.type == "active"}
          onChange={() =>
            updateUser({
              userId: element?.id,
              updates: {
                active: !(accountStatus.type == "active"),
              },
            })
          }
        />
      </li>
    </ul>
  </div>
);

const EmployeeMobileCard = ({
  element,
  accountStatus,
  isPendingRegistration,
  isResending,
  onResend,
  updateUser,
  t,
}) => (
  <article className="employee-mobile-card">
    <div className="employee-mobile-card__header">
      <Link
        to={`/admin/employee/${element?.id}`}
        className="employee-mobile-card__avatar"
      >
        <Avatar width="56" height="56" avatarUrl={element?.avatar_url} />
      </Link>
      <div className="employee-mobile-card__identity">
        <Link
          to={`/admin/employee/${element?.id}`}
          className="employee-mobile-card__name"
        >
          {element?.name}
        </Link>
        <p className="employee-mobile-card__email mb-0">{element?.email}</p>
      </div>
      <div className="employee-mobile-card__menu">
        <EmployeeActionsMenu
          element={element}
          accountStatus={accountStatus}
          updateUser={updateUser}
          t={t}
          menuId={`employee-menu-mobile-${element?.id}`}
        />
      </div>
    </div>

    <ul className="employee-mobile-card__meta list-unstyled mb-0">
      <li>
        <span className="employee-mobile-card__label">{t("accountStatus")}</span>
        <span
          className="employee-mobile-card__value employee-mobile-card__status"
          style={{ color: accountStatus?.color }}
        >
          <span
            className="fas fa-circle fa-fw font_10 me-2"
            style={{ color: accountStatus?.color }}
            aria-hidden="true"
          ></span>
          {accountStatus?.name}
        </span>
      </li>
      <li>
        <span className="employee-mobile-card__label">
          {t("employeeDepartment")}
        </span>
        <span className="employee-mobile-card__value">
          {element?.department?.name ? element.department.name : "_"}
        </span>
      </li>
      <li>
        <span className="employee-mobile-card__label">{t("lastActivity")}</span>
        <span className="employee-mobile-card__value">
          {element?.last_activity
            ? moment(element.last_activity).fromNow()
            : "_"}
        </span>
      </li>
    </ul>

    {isPendingRegistration && (
      <div className="employee-mobile-card__footer">
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm w-100"
          disabled={isResending}
          onClick={() => onResend(element.email)}
        >
          {isResending ? (
            <span className="fas fa-spinner fa-spin fa-fw me-1"></span>
          ) : null}
          {t("employees:resendInvitation")}
        </button>
      </div>
    )}
  </article>
);

const TableEmployee = ({ usersList }) => {
  const [updateUser] = useUpdateUserMutation();
  const [inviteUser] = useInviteUserMutation();
  const [resendingEmail, setResendingEmail] = useState(null);

  const { t } = useTranslation(["common", "employees"]);

  const handleResendInvitation = async (email) => {
    setResendingEmail(email);
    const result = await inviteUser(email);
    setResendingEmail(null);

    if (result?.error) {
      if (result.error.status === 429) {
        const seconds = result.error?.data?.data?.resend_available_in;
        const hours = secondsToHours(seconds);
        toast.error(
          t("employees:resendInvitationRateLimited", {
            message: result.error?.data?.message,
            hours,
          }),
        );
      } else {
        toast.error(result.error?.data?.message);
      }
      return;
    }

    toast.success(t("invitationSuccess"));
  };

  const employees = usersList?.data || [];

  return (
    <>
      <div className="employees-list-page__desktop-table">
        <Table className="table table-employee">
          <Thead className="table-header">
            <Tr>
              <Th>{t("employeeName")}</Th>
              <Th>{t("inputs.email")}</Th>
              <Th>{t("accountStatus")}</Th>
              <Th>{t("employees:invitation")}</Th>
              <Th>{t("employeeDepartment")}</Th>
              <Th>{t("lastActivity")}</Th>
              <Th className="text-center">{t("actions")}</Th>
            </Tr>
          </Thead>
          <Tbody className="table__content">
            {employees.length ? (
              employees.map((element) => {
                const accountStatus = getAccountStatus(
                  element?.registered,
                  element?.active,
                  t,
                );
                const isPendingRegistration =
                  accountStatus.type === "notResigtered";
                const isResending = resendingEmail === element?.email;

                return (
                  <Tr key={element?.id || element?.email}>
                    <Td>
                      <div className="item employee-table-card">
                        <Link
                          to={`/admin/employee/${element?.id}`}
                          className="flex-shrink-0"
                        >
                          <Avatar
                            width="40"
                            height="40"
                            avatarUrl={element?.avatar_url}
                          />
                        </Link>
                        <Link
                          to={`/admin/employee/${element?.id}`}
                          className="employee-table-card__name bold font_14 font-extrabold"
                        >
                          {element?.name}
                        </Link>
                      </div>
                    </Td>
                    <Td>
                      <div className="item employee-table-card__email">
                        <p className="mb-0 font_14 font-color-grey text-truncate">
                          {element?.email}
                        </p>
                      </div>
                    </Td>
                    <Td>
                      <div className="item d-flex align-items-center flex-nowrap state-green">
                        <span
                          className="fas fa-circle fa-fw font_10 me-2"
                          style={{ color: accountStatus?.color }}
                        ></span>
                        <p
                          className="font_14 mb-0"
                          style={{ color: accountStatus?.color }}
                        >
                          {accountStatus?.name}
                        </p>
                      </div>
                    </Td>
                    <Td>
                      <div className="item">
                        {isPendingRegistration ? (
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm text-nowrap"
                            disabled={isResending}
                            onClick={() =>
                              handleResendInvitation(element.email)
                            }
                          >
                            {isResending ? (
                              <span className="fas fa-spinner fa-spin fa-fw me-1"></span>
                            ) : null}
                            {t("employees:resendInvitation")}
                          </button>
                        ) : (
                          <p className="mb-0 font_14 font-color-grey">_</p>
                        )}
                      </div>
                    </Td>
                    <Td>
                      <div className="item">
                        <p className="mb-0 font_14 text-truncate">
                          {element?.department?.name
                            ? element?.department?.name
                            : "_"}
                        </p>
                      </div>
                    </Td>
                    <Td>
                      <div className="item">
                        <p className="mb-0 font_14 ">
                          {element?.last_activity
                            ? moment(element?.last_activity).fromNow()
                            : "_"}
                        </p>
                      </div>
                    </Td>
                    <Td>
                      <div className="item text-center text-md-center">
                        <EmployeeActionsMenu
                          element={element}
                          accountStatus={accountStatus}
                          updateUser={updateUser}
                          t={t}
                          menuId={`employee-menu-${element?.id}`}
                        />
                      </div>
                    </Td>
                  </Tr>
                );
              })
            ) : (
              <tr>
                <td className="border-bottom-0" colSpan={7}>
                  <EmptyEmployees t={t} />
                </td>
              </tr>
            )}
          </Tbody>
        </Table>
      </div>

      <div className="employees-list-page__mobile-cards">
        {employees.length ? (
          <ul className="employee-mobile-cards list-unstyled mb-0">
            {employees.map((element) => {
              const accountStatus = getAccountStatus(
                element?.registered,
                element?.active,
                t,
              );
              const isPendingRegistration =
                accountStatus.type === "notResigtered";
              const isResending = resendingEmail === element?.email;

              return (
                <li key={element?.id || element?.email}>
                  <EmployeeMobileCard
                    element={element}
                    accountStatus={accountStatus}
                    isPendingRegistration={isPendingRegistration}
                    isResending={isResending}
                    onResend={handleResendInvitation}
                    updateUser={updateUser}
                    t={t}
                  />
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="employee-mobile-cards__empty">
            <EmptyEmployees t={t} />
          </div>
        )}
      </div>
    </>
  );
};

export default TableEmployee;
